import { useState } from "react";

import PropTypes from "prop-types";

import globalToasterRef from "@/base/globalToasterRef";
import AddContentIcon from "@/icons/AddContentIcon";
import DeleteContentIcon from "@/icons/DeleteContentIcon";
import ActionPopOutOption from "@/view/ActionPopOutOption";
import contentTypes from "@/view/constants/contentTypes";
import findMatchingTextSetMember from "@/view/utils/findMatchingTextSetMember";
import getEventPropertiesContentType from "@/view/utils/getEventPropertiesContentType";
import getLanguageCode from "@/view/utils/getLanguageCode";
import useAddContentToTextSet from "@/view/utils/useAddContentToTextSet";
import useDeleteContentInTextSet from "@/view/utils/useDeleteContentInTextSet";
import {
  DELETE_CONTENT_SUCCESS_MESSAGE,
  ADD_CONTENT_SUCCESS_MESSAGE,
} from "./UpdateTextSetOption.constants";

const propTypes = {
  contentId: PropTypes.string,
  contentType: PropTypes.string,
  currentLevel: PropTypes.object,
  textSet: PropTypes.shape({
    id: PropTypes.number,
    content_id: PropTypes.string,
    label: PropTypes.string,
    title: PropTypes.string,
    members: PropTypes.arrayOf(
      PropTypes.shape({
        content_id: PropTypes.string,
      }),
    ),
  }),
  toastText: PropTypes.string,
};

const UpdateTextSetOption = ({
  contentId,
  currentLevel,
  contentType,
  textSet,
}) => {
  const textSetMember = findMatchingTextSetMember(
    textSet,
    contentId,
    contentType,
    currentLevel,
  );
  const [addContentToTextSet] = useAddContentToTextSet();
  const [deleteContentFromTextSet] = useDeleteContentInTextSet();
  const [inTextSet, setInTextSet] = useState(textSetMember);
  const [toastProps, setToastProps] = useState(null);
  const contentIsVideo = contentType === contentTypes.INTERACTIVE_VIDEO;

  const getToastErrorMessage = () => {
    return `${contentIsVideo ? "Interactive video" : "Article"} cannot be deleted from text set`;
  };

  const eventProperties = {
    Update: "UpdateTextSetOption",
    objectType: "Content",
    streamProperties: {
      content_type: getEventPropertiesContentType(contentType),
      description: "Add or Delete text set",
      content_id: contentIsVideo ? contentId : currentLevel?.id,
    },
  };

  const actionIcon = inTextSet ? <DeleteContentIcon /> : <AddContentIcon />;
  const containerId = textSet?.content_id;

  const handleClick = (e) => {
    e.preventDefault();
    const updateParams = {
      container_id: containerId,
      member_id: textSetMember?.content_id || contentId,
      language: getLanguageCode(currentLevel?.language),
    };
    if (inTextSet) {
      deleteContentFromTextSet(updateParams)
        .then(() =>
          setToastProps({
            children: `${DELETE_CONTENT_SUCCESS_MESSAGE} ${textSet.title}`,
            statusColor: "success",
          }),
        )
        .catch(() =>
          setToastProps({
            children: getToastErrorMessage(),
            statusColor: "error",
          }),
        );
    } else {
      addContentToTextSet(updateParams)
        .then(() =>
          setToastProps({
            children: `${ADD_CONTENT_SUCCESS_MESSAGE} ${textSet.title}`,
            statusColor: "success",
          }),
        )
        .catch(() =>
          setToastProps({
            children: getToastErrorMessage(),
            statusColor: "error",
          }),
        );
    }
    setInTextSet(!inTextSet);
  };

  const displayToast = () => {
    globalToasterRef.current.displayToast({ ...toastProps, showIcon: "true" });
    setToastProps(null);
  };

  return (
    <>
      <ActionPopOutOption
        icon={actionIcon}
        onClick={handleClick}
        title={textSet.title}
        eventProperties={eventProperties}
      />
      {toastProps && displayToast()}
    </>
  );
};

UpdateTextSetOption.propTypes = propTypes;

export default UpdateTextSetOption;
